import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GlassCard from "@/components/GlassCard";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Home, Loader2, Mail, ArrowLeft, ShieldAlert, Clock, KeyRound, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { isNetworkError } from "@/lib/network";

// ─── Auth Lockout Constants ───────────────────────────────────────────────────
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const STORAGE_KEY_ATTEMPTS = "homeezi_auth_attempts";
const STORAGE_KEY_LOCKOUT_UNTIL = "homeezi_auth_lockout_until";

function getAuthState() {
    const attempts = parseInt(localStorage.getItem(STORAGE_KEY_ATTEMPTS) || "0", 10);
    const lockoutUntil = parseInt(localStorage.getItem(STORAGE_KEY_LOCKOUT_UNTIL) || "0", 10);
    return { attempts, lockoutUntil };
}

function recordFailedAttempt() {
    const { attempts } = getAuthState();
    const newAttempts = attempts + 1;
    localStorage.setItem(STORAGE_KEY_ATTEMPTS, String(newAttempts));
    if (newAttempts >= MAX_ATTEMPTS) {
        const until = Date.now() + LOCKOUT_DURATION_MS;
        localStorage.setItem(STORAGE_KEY_LOCKOUT_UNTIL, String(until));
    }
    return newAttempts;
}

function clearAuthLockout() {
    localStorage.removeItem(STORAGE_KEY_ATTEMPTS);
    localStorage.removeItem(STORAGE_KEY_LOCKOUT_UNTIL);
}

function formatCountdown(msLeft) {
    const totalSeconds = Math.ceil(msLeft / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Only ever redirect to a same-origin relative path after login. Rejects
// missing/empty values, protocol-relative URLs ("//evil.com"), and anything
// that isn't rooted at "/", so the `redirect` query param can't be abused
// for an open redirect.
function getSafeRedirectPath(target) {
    if (!target || typeof target !== "string") return "/";
    if (!target.startsWith("/") || target.startsWith("//")) return "/";
    return target;
}

const Auth = () => {
    const navigate = useNavigate();
    const { signIn, signUp, resendVerification, session, loading: authLoading, isPasswordRecovery, setIsPasswordRecovery } = useAuth();
    const [searchParams] = useSearchParams();
    
    // Disable signups during beta
    const [isSignUp, setIsSignUp] = useState(false);
    
    const [email, setEmail] = useState(searchParams.get("email") || "");
    const [fullName, setFullName] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showCheckInbox, setShowCheckInbox] = useState(false);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [isResetMode, setIsResetMode] = useState(false);

    // Waitlist states
    const [isWaitlist, setIsWaitlist] = useState(searchParams.get("mode") === "signup");
    const [showWaitlistSuccess, setShowWaitlistSuccess] = useState(false);

    // Lockout state
    const [isLockedOut, setIsLockedOut] = useState(false);
    const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);
    const [countdown, setCountdown] = useState("");

    useEffect(() => {
        if (isPasswordRecovery) {
            setIsResetMode(true);
            setIsForgotPassword(false);
            setIsSignUp(false);
            setIsWaitlist(false);
            setShowWaitlistSuccess(false);
        } else if (!authLoading && session) {
            navigate(getSafeRedirectPath(searchParams.get("redirect")));
        }
    }, [session, authLoading, navigate, isPasswordRecovery, searchParams]);

    // Check and update lockout on mount and every second
    useEffect(() => {
        function tick() {
            const { attempts, lockoutUntil } = getAuthState();
            const now = Date.now();
            if (lockoutUntil > now) {
                setIsLockedOut(true);
                setCountdown(formatCountdown(lockoutUntil - now));
            } else {
                if (lockoutUntil > 0 && lockoutUntil <= now) {
                    // Lockout expired — reset
                    clearAuthLockout();
                }
                setIsLockedOut(false);
                setCountdown("");
                setAttemptsLeft(Math.max(0, MAX_ATTEMPTS - attempts));
            }
        }
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        // Block if locked out
        if (isLockedOut) {
            toast.error(`Account locked. Please wait ${countdown}.`);
            return;
        }

        if (!email || !password || (isSignUp && !fullName.trim())) {
            toast.error("Please fill in all fields");
            return;
        }

        setLoading(true);
        try {
            if (isSignUp) {
                const { error } = await signUp(email, password, fullName.trim());
                if (error) {
                    if (isNetworkError(error)) {
                        toast.error("Unable to connect to network. Please check your internet connection and try again.");
                    } else {
                        recordFailedAttempt();
                        toast.error(error.message || "Failed to create account");
                    }
                } else {
                    clearAuthLockout();
                    setShowCheckInbox(true);
                }
            } else {
                const { error } = await signIn(email, password);
                if (error) {
                    if (isNetworkError(error)) {
                        toast.error("Unable to connect to network. Please check your internet connection and try again.");
                    } else {
                        const newCount = recordFailedAttempt();
                        const remaining = Math.max(0, MAX_ATTEMPTS - newCount);
                        if (remaining > 0) {
                            toast.error(`Invalid email or password. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`);
                        } else {
                            toast.error("Too many failed attempts. Account locked for 15 minutes.");
                        }
                    }
                } else {
                    clearAuthLockout();
                    toast.success("Welcome back!");
                    navigate(getSafeRedirectPath(searchParams.get("redirect")));
                }
            }
        } catch (err) {
            console.error(err);
            if (isNetworkError(err)) {
                toast.error("Unable to connect to network. Please check your internet connection and try again.");
            } else {
                recordFailedAttempt();
                toast.error("An unexpected error occurred.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleWaitlistSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        if (!email.trim() || !fullName.trim()) {
            toast.error("Please fill in all fields");
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.from("waitlist").insert({
                email: email.trim().toLowerCase(),
                name: fullName.trim()
            });
            if (error) {
                if (error.code === "23505") {
                    toast.success("You are already on the waitlist! We will notify you soon.");
                    setShowWaitlistSuccess(true);
                } else {
                    toast.error(error.message || "Failed to join waitlist");
                }
            } else {
                toast.success("Successfully joined the waitlist!");
                setShowWaitlistSuccess(true);
            }
        } catch (err) {
            console.error(err);
            toast.error("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error("Please enter your email address");
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/auth`,
            });
            if (error) {
                toast.error(error.message);
            } else {
                setShowCheckInbox(true);
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to send reset email");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPasswordSubmit = async (e) => {
        e.preventDefault();
        if (!password || password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({ password });
            if (error) {
                toast.error(error.message);
            } else {
                toast.success("Password updated successfully!");
                setIsPasswordRecovery(false);
                setIsResetMode(false);
                // Clear the hash from the URL
                window.history.replaceState(null, '', window.location.pathname);
                navigate("/");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to update password");
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!email) return;
        setLoading(true);
        const { error } = await resendVerification(email);
        setLoading(false);
        if (error) toast.error(error.message || "Failed to resend");
        else toast.success("Verification email sent!");
    };

    return (
        <div className="min-h-screen bg-[#fcfcfc] flex items-center justify-center p-6 relative overflow-hidden">
            {/* Animated Background Orbs */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <motion.div
                    animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute -top-[20%] -left-[10%] w-[60%] aspect-square bg-primary/5 rounded-full blur-[120px]"
                />
                <motion.div
                    animate={{ scale: [1.2, 1, 1.2], rotate: [0, -90, 0] }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                    className="absolute -bottom-[20%] -right-[10%] w-[70%] aspect-square bg-[#acc8a2]/10 rounded-full blur-[120px]"
                />
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                className="w-full max-w-sm z-10"
            >
                <div className="flex flex-col items-center mb-10">
                    <Logo size="xl" className="mb-2 scale-110" />
                    <p className="text-[12px] tracking-[0.4em] text-muted-foreground font-bold uppercase mt-2">
                        Your Home, Pocket-Sized
                    </p>
                </div>

                <AnimatePresence mode="wait">
                    {showWaitlistSuccess ? (
                        <motion.div
                            key="waitlist-success"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                        >
                            <GlassCard className="p-8 border-none shadow-[0_20px_50px_rgba(0,0,0,0.05)] rounded-[2.5rem] text-center">
                                <div className="w-16 h-16 mx-auto bg-primary/5 rounded-3xl flex items-center justify-center mb-6">
                                    <Sparkles className="w-8 h-8 text-primary animate-pulse" />
                                </div>
                                <h2 className="text-xl font-extrabold text-foreground tracking-tight">You're on the list!</h2>
                                <p className="text-sm text-balance text-muted-foreground mt-4 leading-relaxed px-2">
                                    Thank you for your interest. We've registered <span className="font-bold text-foreground">{email}</span>.
                                    We'll notify you as soon as early access slots become available.
                                </p>
                                <div className="mt-8 space-y-4">
                                    <Button
                                        variant="outline"
                                        className="w-full h-14 rounded-2xl font-bold text-sm tracking-wide border-gray-100 hover:bg-gray-50 flex items-center justify-center gap-2"
                                        onClick={() => {
                                            setShowWaitlistSuccess(false);
                                            setIsWaitlist(false);
                                            setEmail("");
                                            setFullName("");
                                        }}
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        Back to Sign In
                                    </Button>
                                </div>
                            </GlassCard>
                        </motion.div>
                    ) : isWaitlist ? (
                        <motion.div
                            key="waitlist"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                        >
                            <GlassCard className="p-8 border-none shadow-[0_20px_50px_rgba(0,0,0,0.05)] rounded-[2.5rem]">
                                <div className="text-center mb-6">
                                    <div className="w-16 h-16 mx-auto bg-primary/5 rounded-3xl flex items-center justify-center mb-6">
                                        <Sparkles className="w-8 h-8 text-primary" />
                                    </div>
                                    <h2 className="text-xl font-extrabold text-foreground tracking-tight">Join the Waitlist</h2>
                                    <p className="text-xs text-muted-foreground mt-2">Enter your details to request early access</p>
                                </div>
                                <form onSubmit={handleWaitlistSubmit} className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="space-y-1.5 px-1">
                                            <Label htmlFor="waitlistName" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Full Name</Label>
                                            <Input
                                                id="waitlistName"
                                                type="text"
                                                value={fullName}
                                                onChange={(e) => setFullName(e.target.value)}
                                                placeholder="e.g. David Smith"
                                                className="h-12 bg-gray-50/50 border-none rounded-xl px-4 text-sm focus-visible:ring-1 focus-visible:ring-primary/20 placeholder:text-gray-300"
                                                required
                                                maxLength={100}
                                            />
                                        </div>
                                        <div className="space-y-1.5 px-1">
                                            <Label htmlFor="waitlistEmail" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Email Address</Label>
                                            <Input
                                                id="waitlistEmail"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="e.g. david@homeezi.co.za"
                                                className="h-12 bg-gray-50/50 border-none rounded-xl px-4 text-sm focus-visible:ring-1 focus-visible:ring-primary/20 placeholder:text-gray-300"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full h-14 rounded-2xl font-bold text-sm tracking-wide shadow-lg shadow-primary/10 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            "Join the Waitlist"
                                        )}
                                    </Button>
                                </form>

                                <div className="mt-8 pt-6 border-t border-gray-50 text-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsWaitlist(false);
                                            setFullName("");
                                        }}
                                        className="text-xs font-bold uppercase tracking-widest text-primary/60 hover:text-primary transition-colors flex items-center justify-center gap-2 mx-auto"
                                    >
                                        <ArrowLeft className="w-3 h-3" /> Back to Sign In
                                    </button>
                                </div>
                            </GlassCard>
                        </motion.div>
                    ) : showCheckInbox ? (
                        <motion.div
                            key="check-inbox"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <GlassCard className="p-10 text-center border-none shadow-[0_20px_50px_rgba(0,0,0,0.05)]">
                                <div className="w-16 h-16 mx-auto bg-primary/5 rounded-3xl flex items-center justify-center mb-8">
                                    <Mail className="w-8 h-8 text-primary" />
                                </div>
                                <h1 className="text-2xl font-extrabold text-foreground tracking-tight mb-3">Check Your Inbox</h1>
                                <p className="text-sm text-balance text-muted-foreground mb-8 leading-relaxed">
                                    A verification link has been sent to <span className="font-bold text-foreground">{email}</span>.
                                    Please click it to activate your account.
                                </p>

                                <div className="space-y-4">
                                    <Button
                                        variant="outline"
                                        className="w-full h-14 rounded-2xl font-bold text-sm tracking-wide border-gray-100 hover:bg-gray-50 flex items-center justify-center gap-2"
                                        onClick={() => {
                                            setShowCheckInbox(false);
                                            setIsSignUp(false);
                                        }}
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        Back to Sign In
                                    </Button>

                                    <button
                                        onClick={handleResend}
                                        disabled={loading}
                                        className="text-xs font-bold uppercase tracking-widest text-primary/60 hover:text-primary transition-colors disabled:opacity-30"
                                    >
                                        {loading ? "Sending..." : "Resend Email"}
                                    </button>
                                </div>
                            </GlassCard>
                        </motion.div>
                    ) : isResetMode ? (
                        <motion.div
                            key="reset-password"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                        >
                            <GlassCard className="p-8 border-none shadow-[0_20px_50px_rgba(0,0,0,0.05)] rounded-[2.5rem]">
                                <div className="text-center mb-6">
                                    <h2 className="text-xl font-extrabold text-foreground tracking-tight">Set New Password</h2>
                                    <p className="text-xs text-muted-foreground mt-2">Enter your new password below.</p>
                                </div>
                                <form onSubmit={handleResetPasswordSubmit} className="space-y-6">
                                    <div className="space-y-1.5 px-1">
                                        <Label htmlFor="new-password" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">New Password</Label>
                                        <Input
                                            id="new-password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="h-12 bg-gray-50/50 border-none rounded-xl px-4 text-sm focus-visible:ring-1 focus-visible:ring-primary/20 placeholder:text-gray-300"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full h-14 rounded-2xl font-bold text-sm tracking-wide shadow-lg shadow-primary/10 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                        disabled={loading}
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update Password"}
                                    </Button>
                                </form>
                            </GlassCard>
                        </motion.div>
                    ) : isForgotPassword ? (
                        <motion.div
                            key="forgot-password"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                        >
                            <GlassCard className="p-8 border-none shadow-[0_20px_50px_rgba(0,0,0,0.05)] rounded-[2.5rem]">
                                <div className="text-center mb-6">
                                    <div className="w-12 h-12 mx-auto bg-primary/5 rounded-2xl flex items-center justify-center mb-4">
                                        <KeyRound className="w-6 h-6 text-primary" />
                                    </div>
                                    <h2 className="text-xl font-extrabold text-foreground tracking-tight">Reset Password</h2>
                                    <p className="text-xs text-muted-foreground mt-2 px-4">Enter your email and we'll send you a link to reset your password.</p>
                                </div>

                                <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
                                    <div className="space-y-1.5 px-1">
                                        <Label htmlFor="forgot-email" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Email Address</Label>
                                        <Input
                                            id="forgot-email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="e.g. david@homeezi.co.za"
                                            className="h-12 bg-gray-50/50 border-none rounded-xl px-4 text-sm focus-visible:ring-1 focus-visible:ring-primary/20 placeholder:text-gray-300"
                                            required
                                        />
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full h-14 rounded-2xl font-bold text-sm tracking-wide shadow-lg shadow-primary/10 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                        disabled={loading}
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
                                    </Button>
                                </form>

                                <div className="mt-8 pt-6 border-t border-gray-50 text-center">
                                    <button
                                        onClick={() => setIsForgotPassword(false)}
                                        className="text-xs font-bold uppercase tracking-widest text-primary/60 hover:text-primary transition-colors flex items-center justify-center gap-2 mx-auto"
                                    >
                                        <ArrowLeft className="w-3 h-3" /> Back to Sign In
                                    </button>
                                </div>
                            </GlassCard>
                        </motion.div>
                    ) : (
                        <motion.div
                            key={isSignUp ? "signup" : "signin"}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                        >
                            {/* Lockout Banner */}
                            <AnimatePresence>
                                {isLockedOut && (
                                    <motion.div
                                        key="lockout-banner"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mb-4 overflow-hidden"
                                    >
                                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-100">
                                            <ShieldAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-red-700">Account Temporarily Locked</p>
                                                <p className="text-xs text-red-500 mt-0.5 leading-relaxed">
                                                    Too many failed attempts. Please try again in:
                                                </p>
                                                <div className="flex items-center gap-1.5 mt-2">
                                                    <Clock className="w-4 h-4 text-red-400" />
                                                    <span className="text-base font-extrabold text-red-600 tabular-nums">{countdown}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Attempts warning (when getting close) */}
                            <AnimatePresence>
                                {!isLockedOut && attemptsLeft < MAX_ATTEMPTS && attemptsLeft > 0 && (
                                    <motion.div
                                        key="attempts-warning"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mb-4 overflow-hidden"
                                    >
                                        <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
                                            <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0" />
                                            <p className="text-xs font-semibold text-amber-700">
                                                {attemptsLeft} attempt{attemptsLeft === 1 ? "" : "s"} remaining before 15-minute lockout.
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <GlassCard className="p-8 border-none shadow-[0_20px_50px_rgba(0,0,0,0.05)] rounded-[2.5rem]">
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="space-y-5">
                                        <AnimatePresence>
                                            {isSignUp && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
                                                    animate={{ opacity: 1, height: 'auto', overflow: 'visible' }}
                                                    exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
                                                    className="space-y-1.5 px-1"
                                                >
                                                    <Label htmlFor="fullName" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Full Name</Label>
                                                    <Input
                                                        id="fullName"
                                                        type="text"
                                                        value={fullName}
                                                        onChange={(e) => setFullName(e.target.value)}
                                                        placeholder="e.g. David Smith"
                                                        className="h-12 bg-gray-50/50 border-none rounded-xl px-4 text-sm focus-visible:ring-1 focus-visible:ring-primary/20 placeholder:text-gray-300"
                                                        required={isSignUp}
                                                        disabled={isLockedOut}
                                                        maxLength={100}
                                                    />
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <div className="space-y-1.5 px-1">
                                            <Label htmlFor="email" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Email Address</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="e.g. david@homeezi.co.za"
                                                className="h-12 bg-gray-50/50 border-none rounded-xl px-4 text-sm focus-visible:ring-1 focus-visible:ring-primary/20 placeholder:text-gray-300"
                                                required
                                                disabled={isLockedOut}
                                            />
                                        </div>

                                        <div className="space-y-1.5 px-1">
                                            <Label htmlFor="password" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Password</Label>
                                            <Input
                                                id="password"
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="••••••••"
                                                className="h-12 bg-gray-50/50 border-none rounded-xl px-4 text-sm focus-visible:ring-1 focus-visible:ring-primary/20 placeholder:text-gray-300"
                                                required
                                                minLength={6}
                                                disabled={isLockedOut}
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        id="auth-submit-btn"
                                        className="w-full h-14 rounded-2xl font-bold text-sm tracking-wide shadow-lg shadow-primary/10 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                        disabled={loading || isLockedOut}
                                    >
                                        {loading ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : isLockedOut ? (
                                            `Locked — ${countdown}`
                                        ) : isSignUp ? (
                                            "Get Started"
                                        ) : (
                                            "Enter Your Home"
                                        )}
                                    </Button>

                                    {!isSignUp && (
                                        <div className="text-center mt-4">
                                            <button
                                                type="button"
                                                onClick={() => setIsForgotPassword(true)}
                                                className="text-[11px] font-bold text-muted-foreground hover:text-primary transition-colors"
                                            >
                                                Forgot Password?
                                            </button>
                                        </div>
                                    )}
                                </form>

                                <div className="mt-8 pt-6 border-t border-gray-50 text-center px-4">
                                    <p className="text-xs font-semibold text-muted-foreground leading-relaxed">
                                        HomeEzi is currently in private beta. Public registrations are closed.
                                    </p>
                                    <p className="text-xs mt-2 font-semibold">
                                        Want to join the beta?{" "}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsWaitlist(true);
                                                setFullName("");
                                            }}
                                            className="text-primary hover:underline font-bold"
                                        >
                                            Join the Waitlist
                                        </button>
                                    </p>
                                </div>
                            </GlassCard>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Footer Legal Links */}
                <div className="mt-12 flex items-center justify-center gap-6 px-4">
                    <button onClick={() => navigate('/terms')} className="text-[10px] font-bold uppercase tracking-widest text-gray-300 hover:text-gray-400">Terms</button>
                    <div className="w-1 h-1 rounded-full bg-gray-100" />
                    <button onClick={() => navigate('/privacy')} className="text-[10px] font-bold uppercase tracking-widest text-gray-300 hover:text-gray-400">Privacy</button>
                    <div className="w-1 h-1 rounded-full bg-gray-100" />
                    <a href="mailto:support@homeezi.co.za" className="text-[10px] font-bold uppercase tracking-widest text-gray-300 hover:text-gray-400">Help</a>
                </div>
            </motion.div>
        </div>
    );
};

export default Auth;
